import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Copy, Download, Home, Share2, UserRoundPlus, X } from 'lucide-react';
import { api, post } from './api';
import { text, type Locale } from '../shared/content';
import type { Character, Decor } from '../shared/types';
import RoomScene from './scene/RoomScene';
import { errorText } from './i18n';
const copy = {
  title: text(
    'A little story worth sharing',
    '值得分享的小故事',
    '值得分享的小故事',
    'Una pequeña historia para compartir',
  ),
  info: text(
    'Create a public snapshot of your room and completed chapters. Your cash, email and private messages stay out of the card. You can withdraw this link anytime.',
    '创建公开分享卡，展示房间和已完成的故事；不包含现金、邮箱和私聊。你可以随时撤回链接。',
    '建立公開分享卡，展示房間和已完成的故事；不包含現金、信箱和私訊。你可以隨時撤回連結。',
    'Crea una imagen pública de tu hogar y capítulos completados. No incluye efectivo, correo ni mensajes privados. Puedes retirar el enlace cuando quieras.',
  ),
  create: text('Create public share card', '创建公开分享卡', '建立公開分享卡', 'Crear tarjeta pública'),
  copy: text('Copy link', '复制链接', '複製連結', 'Copiar enlace'),
  share: text('Share story', '分享故事', '分享故事', 'Compartir historia'),
  download: text('Save my 3D room', '保存我的 3D 房间图', '儲存我的 3D 房間圖', 'Guardar mi hogar 3D'),
  copied: text('Share link copied.', '分享链接已复制。', '分享連結已複製。', 'Enlace copiado.'),
  manage: text('Your active share links', '有效分享链接', '有效分享連結', 'Tus enlaces activos'),
  activeInfo: text(
    'All your active links are listed here, up to 30. Withdrawn links are removed from this list.',
    '这里列出你的全部有效链接，最多 30 条。撤回后，链接会从列表中移除。',
    '這裡列出你的全部有效連結，最多 30 條。撤回後，連結會從清單中移除。',
    'Aquí aparecen todos tus enlaces activos, hasta 30. Los enlaces retirados desaparecen de la lista.',
  ),
  empty: text(
    'You have no active share links.',
    '目前没有有效的分享链接。',
    '目前沒有有效的分享連結。',
    'No tienes enlaces activos.',
  ),
  revoke: text('Withdraw link', '撤回链接', '撤回連結', 'Retirar enlace'),
  close: text('Close', '关闭', '關閉', 'Cerrar'),
  newYork: text(
    'A life in New York, at my own pace.',
    '在纽约，按自己的节奏生活。',
    '在紐約，按自己的節奏生活。',
    'Una vida en Nueva York, a mi ritmo.',
  ),
  join: text('Start your own story', '开始你自己的故事', '開始你自己的故事', 'Empieza tu propia historia'),
  friend: text('Say hello to this neighbor', '认识这位邻居', '認識這位鄰居', 'Saluda a este vecino'),
  unavailable: text(
    'This share link is no longer available.',
    '这条分享链接已失效。',
    '這條分享連結已失效。',
    'Este enlace ya no está disponible.',
  ),
  fictional: text(
    'A fictional in-game life · American Dream Online',
    '虚构的游戏人生 · American Dream Online',
    '虛構的遊戲人生 · American Dream Online',
    'Una vida ficticia en el juego · American Dream Online',
  ),
};
export function ShareDialog({
  locale,
  character,
  onClose,
  onMessage,
}: {
  locale: Locale;
  character: Character;
  onClose: () => void;
  onMessage: (s: string, error?: boolean) => void;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [id, setId] = useState<string>(),
    [cards, setCards] = useState<{ id: string; created_at: string }[]>([]),
    [cardsLoaded, setCardsLoaded] = useState(false),
    [busy, setBusy] = useState(false);
  const capture = useRef<(() => Promise<Blob | null>) | undefined>(undefined);
  const refresh = async () => {
    setCards((await api('/shares')).shares);
    setCardsLoaded(true);
  };
  useEffect(() => {
    refresh().catch((error) => onMessage(errorText(locale, error), true));
  }, []);
  const url = id ? `${location.origin}/?share=${id}` : '';
  async function create() {
    setBusy(true);
    try {
      const r = await post('/shares', { kind: 'home' });
      setId(r.id);
      await refresh();
    } catch (e) {
      onMessage(errorText(locale, e), true);
    } finally {
      setBusy(false);
    }
  }
  async function download() {
    const blob = await capture.current?.();
    if (!blob) return;
    const link = document.createElement('a');
    const u = URL.createObjectURL(blob);
    link.href = u;
    link.download = 'my-new-york-room.png';
    link.click();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  }
  return (
    <div className="modal-scrim">
      <section className="modal share-dialog">
        <button className="drawer-close" aria-label={t('close')} onClick={onClose}>
          <X size={19} />
        </button>
        <div className="section-eyebrow">SMALL MOMENTS, BIG MEMORIES</div>
        <h2>{t('title')}</h2>
        <RoomScene
          decor={character.data.decor}
          home={character.data.home}
          locale={locale}
          captureRef={capture}
        />
        <p className="muted small">{t('info')}</p>
        {id ? (
          <>
            <img className="share-card-preview" src={`/api/shares/${id}/card.svg`} alt={t('title')} />
            <div className="share-buttons">
              <button
                className="button primary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(url);
                    onMessage(t('copied'));
                  } catch {
                    onMessage(url);
                  }
                }}
              >
                <Copy size={14} />
                {t('copy')}
              </button>
              {!!navigator.share && (
                <button
                  className="button soft"
                  onClick={async () => {
                    try {
                      await navigator.share({
                        url,
                        title: 'American Dream Online',
                        text: `${character.name} · ${t('newYork')}`,
                      });
                    } catch {}
                  }}
                >
                  <Share2 size={14} />
                  {t('share')}
                </button>
              )}
            </div>
          </>
        ) : (
          <button className="button primary full" disabled={busy} onClick={create}>
            <Share2 size={15} />
            {t('create')}
          </button>
        )}
        <button className="button light full" onClick={download}>
          <Download size={14} />
          {t('download')}
        </button>
        {cardsLoaded && (
          <>
            <h3>{t('manage')}</h3>
            <p className="muted small">{t('activeInfo')}</p>
            {!cards.length && <p className="muted small">{t('empty')}</p>}
            {cards.map((c) => (
              <div className="published-share" key={c.id}>
                <span>
                  {new Intl.DateTimeFormat(locale, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).format(new Date(c.created_at))}
                </span>
                <button
                  className="text-button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await post('/shares/revoke', { id: c.id });
                      if (id === c.id) setId(undefined);
                      await refresh();
                    } catch (e) {
                      onMessage(errorText(locale, e), true);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t('revoke')}
                </button>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );
}
export function PublicShare({
  id,
  locale,
  hasCharacter,
  onJoin,
  onFriend,
}: {
  id: string;
  locale: Locale;
  hasCharacter: boolean;
  onJoin: () => void;
  onFriend: (id: string) => void;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [card, setCard] = useState<{
      snapshot: { name: string; home: string; decor: Decor[]; goals: string[] };
      ownerId: string;
    } | null>(),
    [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    api(`/shares/${id}`)
      .then(setCard)
      .catch(() => setUnavailable(true));
  }, [id]);
  return (
    <div className="public-share-page">
      <a className="public-share-brand" href="/">
        AMERICAN DREAM <small>ONLINE · NEW YORK</small>
      </a>
      <section>
        {unavailable ? (
          <>
            <Home size={35} />
            <h2>{t('unavailable')}</h2>
          </>
        ) : card ? (
          <>
            <div className="section-eyebrow">THE NEW YORK CHAPTER</div>
            <h1>{card.snapshot.name}</h1>
            <p>{t('newYork')}</p>
            <RoomScene decor={card.snapshot.decor} home={card.snapshot.home} locale={locale} />
            <small className="fictional-label">{t('fictional')}</small>
          </>
        ) : (
          <p>…</p>
        )}
        <button className="button primary full" onClick={onJoin}>
          {t('join')}
          <ArrowUpRight size={16} />
        </button>
        {hasCharacter && card && (
          <button className="button soft full" onClick={() => onFriend(card.ownerId)}>
            <UserRoundPlus size={15} />
            {t('friend')}
          </button>
        )}
      </section>
    </div>
  );
}
